<template>
  <div
    id="essay-content-container"
    class="position-relative essay-content-container d-flex flex-column"
  >
    <div v-if="essay.name" class="essay-title text-truncate">
      {{ essay.name }}
    </div>

    <div class="tags-container">
      <icon-badge
        v-for="tag in tags"
        :key="tag.name"
        v-b-tooltip.hover
        class="mr-1 tag-badge"
        :title="tag.name"
      >
        <template v-if="tag.icon" #icon>
          <eg-icon :icon="tag.icon" size="small" />
        </template>
        {{ tag.name }}
      </icon-badge>
    </div>

    <div
      id="highlightContentContainer"
      class="h-100 overflow-auto position-relative"
    >
      <div v-for="(comments, index) in groupedComments" :key="comments.range">
        <b-button
          :id="`commentButton-${comments.range}`"
          pill
          variant="outline-secondary"
          class="comment-button"
          size="sm"
          :style="{ top: `${comments.topOffset}px` }"
        >
          <b-icon icon="chat" font-scale="0.85" />
        </b-button>
        <b-popover
          ref="commentPopover"
          :target="`commentButton-${comments.range}`"
          triggers="focus"
          boundary="scrollParent"
          container="#highlightContentContainer"
        >
          <b-icon
            class="close-icon"
            icon="x"
            @click="$refs.commentPopover?.[index]?.$emit('close')"
          />
        </b-popover>
      </div>

      <highlight-content
        ref="highlightContent"
        class="highlight-content-container h-100"
        :content="essay.content"
        :highlights="showHighlight ? highlights : []"
        :active-highlight-index="activeHighlightIndex"
        @updateActiveHighlightIndex="
          $emit('updateActiveHighlightIndex', $event)
        "
        @addComment="$emit('addComment', $event)"
        @highlightClicked="$emit('highlightClicked', $event)"
      />
    </div>
  </div>
</template>

<script>
import HighlightContent from "@/components/essay/essay/highlight/highlight_content.vue";
import IconBadge from "@/components/global/icon_badge.vue";
import formatDate from "@/utils/formatDate";
import EgIcon from "@/components/global/eg_icon.vue";
import { formatNumber } from "@/utils/format";

export default {
  name: "EssayContent",

  components: {
    HighlightContent,
    IconBadge,
    EgIcon,
  },

  props: {
    essay: {
      type: Object,
      required: true,
    },

    comments: {
      type: Array,
      default: () => [],
    },

    highlights: {
      type: Array,
      default: () => [],
    },

    activeHighlightIndex: {
      type: Number,
      default: -1,
    },

    showHighlight: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      groupedComments: [],
    };
  },

  computed: {
    tags() {
      return [
        {
          name: this.essay.folder.name,
          icon: "folder",
        },
        {
          name: `Graded on ${formatDate(this.essay.createdAt)}`,
          icon: "calendar-date",
        },
        {
          name: `${formatNumber(this.essay.wordCount)} words`,
          icon: "list-paragraph",
        },
      ];
    },
  },

  watch: {
    comments: {
      immediate: true,
      handler() {
        // not computed because has a lag on getCharacterCoordinates
        setTimeout(() => {
          this.groupedComments = this.recalculateCommentsTopOffset(
            this.groupCommentsByRange(this.comments)
          );
        }, 0);
      },
    },
  },

  methods: {
    groupCommentsByRange(comments) {
      // avoid mutating original array and infinity recalculation
      comments = [...comments];

      // Sort comments by starting range values
      comments.sort((a, b) => a.ranges[0] - b.ranges[0]);

      const grouped = [];

      for (const comment of comments) {
        const startRange = comment.ranges[0];

        // If the current comment is not included in the last group, create a new one
        if (
          !grouped.length ||
          startRange > grouped[grouped.length - 1].range + 80
        ) {
          grouped.push({
            range: startRange,
            comments: [comment],
          });
        } else {
          // Add a comment to the last group
          grouped[grouped.length - 1].comments.push(comment);
        }
      }

      return grouped;
    },

    getEssayCommentTopOffset(comment) {
      return (
        this.$refs?.highlightContent?.getCharacterCoordinates(comment.range)
          ?.y || 0
      );
    },

    recalculateCommentsTopOffset(comments) {
      return comments.map((comment) => ({
        ...comment,
        topOffset: this.getEssayCommentTopOffset(comment),
      }));
    },
  },
};
</script>

<style lang="scss" scoped>
.essay-content-container {
  height: 100%;
  border-right: 1px solid lightgray;
}

.comment-button {
  display: flex;
  padding: 4px;
  border: none;
  position: absolute;
  left: 0px;

  &:hover {
    background-color: #fffbd9;
  }

  &[aria-describedby],
  &:focus {
    box-shadow: none;
    background-color: #fffbd9;
  }
}

.close-icon {
  cursor: pointer;
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
}

.essay-title {
  color: var(--color-text-neutral-on-white, #1c0f30);
  padding: 24px 24px 16px 24px;
  font-family: var(--typography-font-family-text-primary, Inter);
  font-size: var(--typography-font-size-fs-600, 20px);
  font-style: normal;
  font-weight: var(--typography-font-weight-fw-600, 600);
  line-height: 24px;
}

.tags-container {
  display: flex;
  padding: 16px 24px 8px 24px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
}

.tag-badge {
  border-radius: var(--radius-rd-500, 16px);
  border: 1px solid var(--color-stroke-neutral-disabled, #d3d9de);
  height: 24px;
}

.highlight-content-container {
  padding: 0 24px;
  color: var(--color-text-neutral-on-white, #1c0f30);
  font-family: var(--typography-font-family-text-primary, Inter);
  font-size: var(--typography-font-size-fs-300, 14px);
  font-style: normal;
  font-weight: var(--typography-font-weight-fw-400, 400);
  line-height: 140%;
  white-space: break-spaces;
}
</style>
